import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [twofaPending, setTwofaPending] = useState(null); // { token, method }

  // Configure axios interceptor
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Load user on mount
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          console.error('Failed to load user:', error);
          logout();
        }
      }
      setLoading(false);
    };
    loadUser();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const data = response.data;
    
    // Check if 2FA is required
    if (data.twofa_required) {
      setTwofaPending({ token: data.access_token, method: data.twofa_method });
      return { twofa_required: true, method: data.twofa_method };
    }
    
    const { access_token, user: userData } = data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const verify2FA = async (code) => {
    if (!twofaPending) throw new Error('No 2FA pending');
    
    const response = await axios.post(`${API}/auth/verify-2fa`, { code }, {
      headers: { Authorization: `Bearer ${twofaPending.token}` }
    });
    
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setTwofaPending(null);
    return userData;
  };

  const cancel2FA = () => {
    setTwofaPending(null);
  };

  const signup = async (email, password, organisation_name, agreed_to_terms = false) => {
    const response = await axios.post(`${API}/auth/signup`, {
      email,
      password,
      organisation_name,
      agreed_to_terms,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return { user: userData, access_token };
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setTwofaPending(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, signup, logout, loading, twofaPending, verify2FA, cancel2FA }}>
      {children}
    </AuthContext.Provider>
  );
};
